import {useCallback, useMemo, useState} from 'react';

import {CommonEventStatusEnum, EventFormType} from '../../../service/events/events-type';
import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../../service/feature-companies/feature-companies-type';
import {Form} from '../../../typings/antd';
import {
    CompanySelectionModeEnum,
    PublicationModeSelectionStep,
} from '../../publication-wizard/publication-mode-selection-step';
import {PublicationSelectCompaniesStep} from '../../publication-wizard/publication-select-companies-step';

import {EventDetailsStep} from './event-details-step';
import {EVENT_DETAILS_STEP_WIDTH, EVENT_MEDIA_STEP_WIDTH, EVENT_SELECT_COMPANIES_STEP_WIDTH} from './event-form-helper';
import {EventMediaStep} from './event-media-step';

type PropsType = {
    onSubmit: (values: EventFormType, status: CommonEventStatusEnum) => Promise<void>;
    onChangeStep: (width: number) => void;
    closeModal: () => void;
    mode: 'edit' | 'create';
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    initialValues?: Partial<EventFormType>;
};

export function EventForm(props: PropsType): JSX.Element {
    const {onSubmit, onChangeStep, closeModal, mode, companiesSelector, initialValues} = props;

    const [form] = Form.useForm<EventFormType>();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [selectionMode, setSelectionMode] = useState<CompanySelectionModeEnum>(CompanySelectionModeEnum.Manual);

    const goToNextStep = useCallback(() => setCurrentStep((previousStep) => previousStep + 1), []);
    const goToPreviousStep = useCallback(
        () => setCurrentStep((previousStep) => (previousStep === 0 ? previousStep : previousStep - 1)),
        []
    );

    const handleFormSubmit = useCallback(
        async (values: EventFormType, status: CommonEventStatusEnum) => {
            await onSubmit(values, status);
            closeModal();
        },
        [closeModal, onSubmit]
    );

    const steps = useMemo(
        () => [
            <PublicationModeSelectionStep
                companiesSelector={companiesSelector}
                key="mode"
                mode={selectionMode}
                onCancel={closeModal}
                onModeChange={setSelectionMode}
                onStepActivated={onChangeStep}
                onStepFinished={goToNextStep}
                width={EVENT_SELECT_COMPANIES_STEP_WIDTH}
            />,
            ...(selectionMode === CompanySelectionModeEnum.Manual
                ? [
                      <PublicationSelectCompaniesStep
                          companiesSelector={companiesSelector}
                          key="companies"
                          onPreviousStep={goToPreviousStep}
                          onStepActivated={onChangeStep}
                          onStepFinished={goToNextStep}
                          width={EVENT_SELECT_COMPANIES_STEP_WIDTH}
                      />,
                  ]
                : []),
            <EventMediaStep
                key="media"
                onPreviousStep={goToPreviousStep}
                onStepActivated={onChangeStep}
                onStepFinished={goToNextStep}
                width={EVENT_MEDIA_STEP_WIDTH}
            />,
            <EventDetailsStep
                formInstance={form}
                formMode={mode}
                key="details"
                onPreviousStep={goToPreviousStep}
                onStepActivated={onChangeStep}
                onSubmit={handleFormSubmit}
                width={EVENT_DETAILS_STEP_WIDTH}
            />,
        ],
        [
            companiesSelector,
            closeModal,
            form,
            goToNextStep,
            goToPreviousStep,
            handleFormSubmit,
            mode,
            onChangeStep,
            selectionMode,
        ]
    );

    return (
        <Form<EventFormType> form={form} initialValues={initialValues} layout="vertical">
            {steps[currentStep]}
        </Form>
    );
}
